/**
 * Recensioni — copy.
 *
 * The review page (/recensione/<token>, /recensione) is Italian only: the
 * request email is Italian and the URL carries no locale. The pieces that
 * live on the sites' own pages (live reviews, the methodology note) follow
 * the page's locale, so they come in both.
 *
 * Rules (research note §1.4, §2.1, §2.7): no incentive, no superlatives, no
 * suggested review text, nothing that depends on the rating. The
 * methodology note must stay true to how the system works — change it
 * together with the backend, never on its own.
 */

export type ReviewsLocale = 'it' | 'en'

/** «Come raccogliamo le recensioni» (Codice del Consumo art. 22 c.5-bis; AGCM draft §15–16). */
export const METHOD_NOTE: Record<ReviewsLocale, { title: string; points: readonly string[] }> = {
  it: {
    title: 'Come raccogliamo le recensioni',
    points: [
      'Le recensioni su questo sito vengono da nostri clienti: dopo un lavoro inviamo al cliente un link personale, che vale solo per lui, con cui può lasciare un voto e un commento.',
      'Pubblichiamo una recensione solo se il cliente ci dà il consenso, con il nome che sceglie lui. Il consenso si può ritirare in qualsiasi momento.',
      'Pubblichiamo tutte le recensioni con il consenso, positive o negative. Ne togliamo una solo se ha contenuti illeciti o offensivi, e annotiamo il motivo.',
      'Il voto medio è la media semplice dei voti pubblicati. Le recensioni sono in ordine di data, dalla più recente.',
      'Non diamo sconti, regali o altri vantaggi in cambio di una recensione.',
    ],
  },
  en: {
    title: 'How we collect reviews',
    points: [
      'The reviews on this site come from our customers: after a job we send the customer a personal link, valid only for them, to leave a rating and a comment.',
      'We publish a review only with the customer’s consent, under the name they choose. Consent can be withdrawn at any time.',
      'We publish every review given with consent, positive or negative. We remove one only if it is unlawful or offensive, and we record the reason.',
      'The average rating is the simple mean of the published ratings. Reviews are listed by date, newest first.',
      'We offer no discounts, gifts or other benefits in exchange for a review.',
    ],
  },
}

/** Labels for the live reviews on the sites' pages. */
export const SITE_REVIEWS: Record<
  ReviewsLocale,
  {
    eyebrow: string
    title: string
    verified: (n: number) => string
    outOf5: (avg: string) => string
    howShort: string
    showAll: (n: number) => string
    showLess: string
    readMore: string
    readLess: string
    readMoreOf: (name: string) => string
    close: string
    stars: (n: number) => string
    tagVerified: string
    tagGoogle: string
    verifiedLine: (n: number, avg: string | null) => string
    googleRatingSr: (avg: string, count: number) => string
    googleTitle: string
    googleLink: string
  }
> = {
  it: {
    eyebrow: 'Recensioni',
    title: 'Cosa dicono i clienti',
    verified: (n) => (n === 1 ? '1 recensione verificata' : `${n} recensioni verificate`),
    outOf5: (avg) => `${avg} su 5`,
    howShort: 'Come le raccogliamo',
    showAll: (n) => `Mostra tutte (${n})`,
    showLess: 'Mostra meno',
    readMore: 'Leggi tutto',
    readLess: 'Riduci',
    readMoreOf: (name) => `Leggi tutta la recensione di ${name}`,
    close: 'Chiudi',
    stars: (n) => (n === 1 ? '1 stella su 5' : `${n} stelle su 5`),
    tagVerified: 'Recensione verificata',
    tagGoogle: 'Da Google',
    verifiedLine: (n, avg) => (avg ? `${n === 1 ? '1 recensione verificata' : `${n} recensioni verificate`} · media ${avg}` : n === 1 ? '1 recensione verificata' : `${n} recensioni verificate`),
    googleRatingSr: (avg, count) => `${avg} su 5 su Google, ${count === 1 ? '1 valutazione' : `${count} valutazioni`}`,
    googleTitle: 'Su Google',
    googleLink: 'Leggi le recensioni su Google',
  },
  en: {
    eyebrow: 'Reviews',
    title: 'What customers say',
    verified: (n) => (n === 1 ? '1 verified review' : `${n} verified reviews`),
    outOf5: (avg) => `${avg} out of 5`,
    howShort: 'How we collect them',
    showAll: (n) => `Show all (${n})`,
    showLess: 'Show fewer',
    readMore: 'Read more',
    readLess: 'Show less',
    readMoreOf: (name) => `Read the full review by ${name}`,
    close: 'Close',
    stars: (n) => (n === 1 ? '1 star out of 5' : `${n} stars out of 5`),
    tagVerified: 'Verified review',
    tagGoogle: 'From Google',
    verifiedLine: (n, avg) => (avg ? `${n === 1 ? '1 verified review' : `${n} verified reviews`} · average ${avg}` : n === 1 ? '1 verified review' : `${n} verified reviews`),
    googleRatingSr: (avg, count) => `${avg} out of 5 on Google, ${count === 1 ? '1 rating' : `${count} ratings`}`,
    googleTitle: 'On Google',
    googleLink: 'Read the reviews on Google',
  },
}

/** The review page (Italian; the customer is addressed with «Lei», like the other token pages). */
export const PAGE = {
  kicker: 'Recensione',
  loading: 'Caricamento…',
  greeting: (name: string) => (name ? `Buongiorno ${name},` : 'Buongiorno,'),
  intro: 'grazie per averci scelto. Ci dice com’è andato il lavoro? Basta un voto; se vuole, può aggiungere qualche riga.',
  ratingLabel: 'Il suo voto',
  ratingHint: 'Da 1 a 5 stelle',
  ratingChosen: (n: number) => `${n} su 5`,
  ratingStar: (n: number) => (n === 1 ? '1 stella' : `${n} stelle`),
  ratingRequired: 'Scelga un voto da 1 a 5 stelle.',
  textLabel: 'La sua recensione',
  textOptional: 'facoltativa',
  textHint: 'Con parole sue: cosa è andato bene, cosa possiamo fare meglio.',
  textCount: (n: number, max: number) => `${n} / ${max} caratteri`,
  nameLabel: 'Il suo nome sul sito',
  // What a published review shows: stars, text, month and year, and the
  // signature — name, plus the town when the request has one.
  namePreview: (shown: string, withTown: boolean) =>
    withTown
      ? `Se acconsente alla pubblicazione, sul sito compariranno il nome e il comune: «${shown}».`
      : `Se acconsente alla pubblicazione, sul sito comparirà il nome: «${shown}».`,
  consent: (domain: string, shown: string, withTown: boolean) =>
    `Acconsento a pubblicare su ${domain} il voto e il testo di questa recensione, con mese e anno, firmati con ${withTown ? 'nome e comune' : 'il nome'}: «${shown}». Posso ritirare il consenso in qualsiasi momento, anche da questa pagina.`,
  consentNote: 'Senza consenso la recensione resta visibile solo a noi.',
  privacy: 'Informativa privacy',
  submit: 'Invia la recensione',
  submitting: 'Invio in corso…',
  submitError: 'Non siamo riusciti a inviare la recensione. Controlli la connessione e riprovi: quello che ha scritto è ancora qui.',
  submitInvalid: 'Controlli i dati inseriti e riprovi.',
  submitThrottled: 'Troppi tentativi in poco tempo. Riprovi tra qualche minuto.',

  thanksTitle: (name: string) => (name ? `Grazie, ${name}!` : 'Grazie!'),
  thanksReceived: 'Abbiamo ricevuto la sua recensione.',
  thanksPublic: (shown: string) => `Comparirà sul nostro sito firmata «${shown}».`,
  thanksPrivate: 'Non è pubblicata sul sito: resta visibile solo a noi.',
  thanksNotVisible: 'Al momento la recensione non è visibile sul sito.',

  withdrawLink: 'Ritira il consenso alla pubblicazione',
  withdrawTitle: 'Ritirare il consenso?',
  withdrawBody: 'La recensione non comparirà più sul sito. Resterà visibile solo a noi.',
  withdrawConfirm: 'Sì, ritira il consenso',
  withdrawSending: 'Un momento…',
  withdrawCancel: 'Annulla',
  withdrawDone: 'Consenso ritirato: la recensione non compare più sul sito.',
  withdrawError: (email: string) => `Non è stato possibile ritirare il consenso. Riprovi, oppure scriva a ${email}.`,

  googleTitle: 'Pubblica anche su Google',
  googleBody: 'Se vuole, può lasciare la stessa recensione anche sulla nostra scheda Google. La pagina di Google si apre in una nuova scheda; serve un account Google.',
  googleButtonCopy: 'Copia il testo e apri Google',
  googleButtonOpen: 'Apri Google',
  googleCopyNote: 'Il pulsante copia negli appunti il testo che ha scritto, così su Google basta incollarlo. Non copiamo nulla finché non lo preme.',
  googleCopied: 'Testo copiato. Su Google lo incolli nel campo della recensione.',
  googleCopyFailed: 'Non è stato possibile copiare il testo in automatico. Eccolo qui sotto: lo selezioni e lo copi.',
  googleTextLabel: 'Il testo della sua recensione',
  newTab: '(si apre in una nuova scheda)',

  expiredTitle: 'Questo link è scaduto',
  expiredBody: 'I link per lasciare una recensione restano attivi 90 giorni.',
  revokedTitle: 'Questo link non è più attivo',
  revokedBody: (sede: string) => `Probabilmente le abbiamo inviato un link più recente: lo trova nell’ultima email ricevuta da ${sede}.`,
  contactUs: 'Se vuole dirci com’è andata, ci scriva o ci chiami:',
  notFoundTitle: 'Link non valido',
  notFoundBody: 'Controlli di aver aperto il link completo ricevuto via email.',
  networkTitle: 'Non riusciamo a caricare la pagina',
  networkBody: 'Controlli la connessione e riprovi.',
  retry: 'Riprova',

  stopLink: 'Non voglio più ricevere richieste',
  stopTitle: 'Non vuole più ricevere richieste di recensione?',
  stopBody: (sede: string) =>
    `${sede} non le invierà più email per chiederle una recensione. Le comunicazioni sui lavori (preventivi, appuntamenti) restano come sono.`,
  stopConfirm: 'Sì, non inviatemi più richieste',
  stopSending: 'Un momento…',
  stopCancel: 'Annulla',
  stopDoneTitle: 'Fatto',
  stopDoneBody: (sede: string) => `Non le invieremo altre richieste di recensione da ${sede}.`,
  stopError: (email: string) => `Non è stato possibile registrare la richiesta. Riprovi, oppure scriva a ${email}.`,
  stopInvalid: 'Questo link non è valido, quindi non possiamo registrare la richiesta da qui.',
  backToPage: 'Torna alla pagina',

  inviteTitle: 'Lasci una recensione',
  inviteBody: (sede: string) => `Grazie per voler raccontare com’è andato il lavoro con ${sede}.`,
  inviteGoogle: 'Può farlo sulla nostra scheda Google:',
  inviteGoogleButton: 'Scrivi una recensione su Google',
  inviteVerified: 'Le recensioni verificate di questo sito si lasciano dal link personale che inviamo via email dopo un lavoro: se l’ha ricevuto, usi quello.',
  backHome: 'Torna al sito',
} as const
